import { useState, useCallback, useMemo, useEffect } from 'react'
import { useAuth } from './useAuth'
import { useWebSocket } from './useWebSocket'
import { logger } from '@magnetar/utils'

export interface DeviceOnlineStatusData {
  onlineDeviceIds: number[]
  deviceLastConnectedMap: Record<number, string | null>
  timestamp: string
}

interface DeviceOnlineStatusMessage {
  type?: string
  success?: boolean
  data: { message?: string } & Partial<DeviceOnlineStatusData>
}

export interface UseDeviceOnlineStatusReturn {
  onlineDeviceIds: number[]
  deviceLastConnectedMap: Record<number, string | null>
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  reconnect: () => void
}

/**
 * 通用的設備在線狀態 Hook
 * 透過 WebSocket 即時取得指定設備的在線狀態和最後連線時間
 *
 * @param deviceIds 要查詢的設備 ID 陣列
 * @returns 在線設備 IDs、最後連線時間映射、連線狀態
 */
export function useDeviceOnlineStatus(deviceIds: number[]): UseDeviceOnlineStatusReturn {
  const [onlineDeviceIds, setOnlineDeviceIds] = useState<number[]>([])
  const [deviceLastConnectedMap, setDeviceLastConnectedMap] = useState<
    Record<number, string | null>
  >({})
  const [error, setError] = useState<string | null>(null)
  const { token } = useAuth()

  // 追蹤 deviceIds 的變化，用於產生 connectionKey
  const deviceIdsKey = useMemo(() => {
    const sortedIds = [...deviceIds].sort((a, b) => a - b)
    return sortedIds.join(',')
  }, [deviceIds])

  // 只在有 deviceIds 時才啟用 WebSocket
  const hasDeviceIds = deviceIds.length > 0

  const wsProtocol =
    typeof window !== 'undefined' && window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const wsHost = import.meta.env.VITE_BROADCAST_SERVICE_URL || 'localhost:9010'
  const wsUrl =
    token && hasDeviceIds
      ? `${wsProtocol}//${wsHost}/admin/ws/device-online-status?token=${encodeURIComponent(token)}&deviceIds=${deviceIdsKey}`
      : ''

  // connectionKey 需要包含 deviceIds，這樣當 deviceIds 變化時會重新連線
  const connectionKey = useMemo(
    () => `device-online-status-${deviceIdsKey}-${token?.slice(0, 10) || 'no-token'}`,
    [deviceIdsKey, token],
  )

  const handleMessage = useCallback((raw: unknown) => {
    try {
      const message = raw as DeviceOnlineStatusMessage
      if (message.type === 'device_online_status') {
        const data = message.data
        setOnlineDeviceIds(data.onlineDeviceIds || [])
        setDeviceLastConnectedMap(data.deviceLastConnectedMap || {})
        setError(null)
        logger.log('收到設備在線狀態更新:', data)
      } else if (message.type === 'error') {
        logger.error('WebSocket 錯誤:', message.data)
        setError(message.data.message || '連接錯誤')
      } else if (message.success === true) {
        logger.log('WebSocket 連接成功:', message.data?.message)
      }
    } catch (err) {
      logger.error('處理 WebSocket 訊息失敗:', err)
      setError('數據處理失敗')
    }
  }, [])

  const { isConnected, isConnecting, reconnect, status } = useWebSocket({
    connectionKey,
    url: wsUrl,
    enabled: !!token && hasDeviceIds,
    onMessage: handleMessage,
  })

  // 當沒有 deviceIds 時，清空狀態
  useEffect(() => {
    if (!hasDeviceIds) {
      setOnlineDeviceIds([])
      setDeviceLastConnectedMap({})
      setError(null)
    }
  }, [hasDeviceIds])

  return {
    onlineDeviceIds,
    deviceLastConnectedMap,
    isConnected,
    isConnecting,
    error: status === 'error' ? error || '連接失敗' : error,
    reconnect,
  }
}
